/**
 * Utilitários de formatação e conversão
 */

/**
 * Converte uma data para o formato de input HTML (YYYY-MM-DD)
 * Retorna null se a data for inválida
 */
export function toInputDate(dateString: string | undefined | null): string | null {
  if (dateString === null || dateString === undefined) return null
  
  if (typeof dateString === 'string') {
    const trimmed = dateString.trim()
    if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return null
    
    try {
      const date = new Date(trimmed)
      if (isNaN(date.getTime())) return null
      
      const year = date.getFullYear()
      if (year < 1900 || year > 2100) return null
      
      return date.toISOString().split('T')[0]
    } catch {
      return null
    }
  }
  
  return null
}

/**
 * Converte uma data para o formato ISO completo para o backend
 * Retorna null se a data for inválida
 */
export function toBackendDate(dateString: string | undefined | null): string | null {
  if (!dateString || dateString.trim() === '' || dateString.trim() === 'undefined') return null
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return date.toISOString()
  } catch {
    return null
  }
}

/**
 * Converte uma data obrigatória para o formato ISO
 * Usa a data atual como fallback se não fornecida
 */
export function toRequiredDate(dateString: string | undefined): string {
  if (!dateString || dateString.trim() === '') {
    return new Date().toISOString()
  }
  
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return new Date().toISOString()
    }
    return date.toISOString()
  } catch {
    return new Date().toISOString()
  }
}

/**
 * Converte um valor para número
 * Retorna defaultValue se o valor for inválido
 */
export function toNumber(value: any, defaultValue: number | null = null): number | null {
  if (value === null || value === undefined || value === '' || value === 'undefined') {
    return defaultValue
  }
  const num = Number(value)
  return isNaN(num) ? defaultValue : num
}

/**
 * Normaliza texto para busca: remove acentos e deixa em minúsculas.
 * Ex.: "Vínicius" e "vinicius" geram a mesma string para comparação.
 */
export function normalizarParaBusca(texto: string | null | undefined): string {
  if (texto == null || typeof texto !== 'string') return ''
  return texto
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Normaliza um campo de data para string vazia se inválido
 */
export function normalizeDateField(value: string | null | undefined): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed === '' || trimmed === 'undefined' || trimmed === 'null' ? '' : trimmed
  }
  return ''
}

/**
 * Formata uma data para o padrão brasileiro dd/MM/yyyy.
 * Aceita strings ISO completas (2026-02-10T03:00:00Z) ou apenas a parte de data (2026-02-10).
 * Retorna string vazia se a data for inválida.
 */
export function formatDateToBrazilian(dateString: string | null | undefined): string {
  if (!dateString) return ''
  const trimmed = dateString.trim()
  if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return ''

  const datePart = trimmed.split('T')[0]
  const parts = datePart.split('-')
  if (parts.length !== 3) return ''

  const [year, month, day] = parts
  if (!year || !month || !day) return ''

  return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
}

/**
 * Extrai mensagem de erro de uma resposta de erro
 */
export function extractErrorMessage(error: any, defaultMessage: string = 'Erro desconhecido'): string {
  if (error?.response?.data) {
    const errorData = error.response.data
    if (typeof errorData === 'string') {
      return errorData
    }
    if (errorData.message) {
      return errorData.message
    }
    if (errorData.error) {
      return errorData.error
    }
  }
  
  if (error?.message) {
    return error.message
  }
  
  return defaultMessage
}

/**
 * Extrai valor de propriedade com suporte a múltiplos nomes (camelCase e PascalCase)
 */
export function getPropValue(obj: any, ...propNames: string[]): any {
  if (!obj) return undefined
  
  for (const propName of propNames) {
    if (obj[propName] !== undefined && obj[propName] !== null) {
      return obj[propName]
    }
  }
  
  return undefined
}

/**
 * Limpa filtros removendo valores undefined, null e vazios
 */
export function cleanFilters(filters: Record<string, any>): Record<string, any> {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, any>)
}
